/**
 * Modelo operativo por defecto de un espacio de posventa (supuestos ilustrativos editables,
 * prompt §6.3 y parámetros por defecto del v6). Nada aquí es una medición real.
 */

import type { ConfiguracionAleatoria, ModeloOperativo } from '../nucleo/dominio/espacio';
import { supuesto } from '../nucleo/dominio/parametros';
import type { DefinicionDeProceso } from '../nucleo/dominio/proceso';
import { procesoPosventa } from './procesos';

/** Perfil «citas» del v6: fracción de citas por hora de 08:00 a 16:00 (44 % antes de las 10:00). */
export const PERFIL_CITAS_V6 = [0.24, 0.2, 0.15, 0.1, 0.06, 0.08, 0.09, 0.08];

/**
 * Perfil escalonado del Taller base (supuesto calibrado en la entrega 1.0): mantiene el pico de la
 * mañana, pero reparte las citas para que la cola de las 9:00 no haga imposible la promesa.
 */
export const PERFIL_CITAS_ESCALONADO = [0.18, 0.16, 0.14, 0.12, 0.1, 0.1, 0.1, 0.1];

/** Perfil «muy concentrado 8–10 h» del v6. */
export const PERFIL_CITAS_CONCENTRADO = [0.35, 0.25, 0.15, 0.08, 0.04, 0.05, 0.04, 0.04];

/** Cómo llegan los autos durante el día (perfiles a elegir, como en el v6). */
export type FormaDeLlegada = 'escalonada' | 'pico-manana' | 'concentrada' | 'pareja';

export const FORMAS_DE_LLEGADA: readonly FormaDeLlegada[] = [
  'escalonada',
  'pico-manana',
  'concentrada',
  'pareja',
];

const BASE_FORMA: Record<FormaDeLlegada, readonly number[]> = {
  escalonada: PERFIL_CITAS_ESCALONADO,
  'pico-manana': PERFIL_CITAS_V6,
  concentrada: PERFIL_CITAS_CONCENTRADO,
  pareja: [1, 1, 1, 1, 1, 1, 1, 1],
};

/**
 * Perfil horario de `horas` horas con la forma elegida: la base de 8 horas se estira o se encoge y
 * se normaliza para que sume 1 (redondeado a milésimas).
 */
export function perfilDeLlegada(forma: FormaDeLlegada, horas: number): number[] {
  const base = BASE_FORMA[forma];
  const n = Math.max(1, Math.round(horas));
  const crudo = Array.from({ length: n }, (_, h) => base[Math.floor((h * base.length) / n)]!);
  const suma = crudo.reduce((a, b) => a + b, 0);
  const r = crudo.map((v) => Math.round((v / suma) * 1000) / 1000);
  // El redondeo puede dejar una milésima de diferencia: se ajusta en la primera hora.
  r[0] = Math.round((r[0]! + 1 - r.reduce((a, b) => a + b, 0)) * 1000) / 1000;
  return r;
}

/** ¿Qué forma tiene un perfil? `null` si es un perfil propio. */
export function formaDePerfil(perfil: readonly number[]): FormaDeLlegada | null {
  for (const forma of FORMAS_DE_LLEGADA) {
    const ref = perfilDeLlegada(forma, perfil.length);
    if (ref.every((v, i) => Math.abs(v - (perfil[i] ?? 0)) < 0.0015)) return forma;
  }
  return null;
}

/** Demanda del Taller base: exigido pero creíble con 6 elevadores (informe 1.0, §13.3). */
export const DEMANDA_TALLER_BASE = 21;

export function modeloPosventa(proceso: DefinicionDeProceso = procesoPosventa()): ModeloOperativo {
  return {
    proceso,
    dotacion: {
      asesor: supuesto(3, 'personas'),
      // Un técnico por elevador (ajuste de Renato al revisar la 1.0).
      tecnico: supuesto(6, 'personas'),
    },
    demanda: {
      modo: 'perfil',
      unidadesPorDia: supuesto(DEMANDA_TALLER_BASE, 'autos/día'),
      perfilHorario: supuesto(PERFIL_CITAS_ESCALONADO, 'fracción por hora'),
      mezcla: supuesto({ mantenimiento: 0.8, otros: 0.2 }, 'fracción'),
    },
    jornada: {
      inicioTurno: supuesto('08:00', 'hh:mm'),
      finTurno: supuesto('18:00', 'hh:mm'),
      inicioCitas: supuesto('08:00', 'hh:mm'),
      ultimaCita: supuesto('16:00', 'hh:mm'),
      cierre: supuesto('19:30', 'hh:mm'),
    },
    atencion: { inicio: supuesto('08:00', 'hh:mm'), fin: supuesto('18:00', 'hh:mm') },
    redondeoPromesaMin: supuesto(15, 'min'),
    movimiento: {
      modo: 'distancia',
      velocidadVehiculoKmh: supuesto(5, 'km/h'),
      maniobraS: supuesto(40, 's'),
      trasladoFijoMin: supuesto(2, 'min'),
    },
    politicas: {
      seleccionEstacion: 'mas-cercana',
      seleccionPersona: 'menor-carga',
      // Aprobado por Renato al cerrar la 1.0: el lavado termina los autos del día.
      lavadoDespuesDeHora: true,
      // Aprobado por Renato el 2026-11-02 (avance 3 de la 1.6): todo lo nuevo se cede el paso. Los
      // proyectos ya guardados no tienen el campo y siguen sin la política.
      cederElPaso: true,
    },
  };
}

export function configuracionAleatoriaPorDefecto(): ConfiguracionAleatoria {
  return { semilla: 1, replicas: 20, replicasAutomaticas: true, dias: 1 };
}
